import React, { useState, useCallback } from 'react'
import styled from 'styled-components'
import PropTypes from 'prop-types'
import { Modal, Form } from 'react-bootstrap'
import { breakpoints, colors } from 'globalStyles'
import BuzzerCode from 'components/BuzzerCode'
import BuzzerCodeIcon from 'assets/svg/buzzer-door-bell.svg'
import Button from 'react-bootstrap/Button'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { selectDeliverySummary } from 'store/shipping/shippingSelectors'

const StyledPopUp = styled(Modal)`
  .modal-dialog {
    display: flex !important;
    align-items: center !important;
  }
  .modal-dialog.modal-dialog-centered {
    justify-content: center;
    align-items: center;
  }
  .modal-content {
    position: relative;
    width: 100%;
  }
  @media ${breakpoints.phone}, ${breakpoints.tablet} {
    .modal-content {
      width: 330px !important;
      height: auto;
      min-height: 348px;
    }
  }
  @media ${breakpoints.desktop} {
    .modal-content {
      width: 500px;
      height: auto;
      min-height: 324px;
    }
  }
  .modal-header {
    background-color: ${colors.translucent_light_blue};
    padding: 0px;
    min-height: 78px;
    justify-content: center;
  }
  .modal-title {
    font-weight: 600;
    font-size: 20px;
    padding: 15px;
  }
  .modal-body {
    text-align: center;
  }

  .modal-footer {
    width: 100% !important;
    border-top: none;
    justify-content: center;
    margin-top: -10px;
    gap: 8px;
    @media ${breakpoints.desktop} {
      gap: 16px;
      margin-bottom: 10px;
    }
  }

  button.popUpSecBtn.btn.btn-secondary,
  button.popUpPrimBtn.btn.btn-primary {
    min-width: 132px !important;
    max-width: 160px !important;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    padding: 8px 16px;
    @media ${breakpoints.sm}, ${breakpoints.tablet} {
      min-width: 140px !important;
      max-width: 160px !important;
    }
    @media ${breakpoints.desktop} {
      min-width: 218px !important;
      max-width: 240px !important;
    }
  }
`

const StyledForm = styled(Form)`
  margin: 0;
  padding: 0;
`

const BuzzerCodePopUp = ({ show, onHide, onSave }) => {
  const { t } = useTranslation()
  const [validated, setValidated] = useState(false)

  const { buzzerCode } = useSelector(selectDeliverySummary)
  const [buzzCode, setBuzzCode] = useState(buzzerCode)

  const buzzCodeValueChangeHandler = (event) => {
    const buzCode = event.target.value
    const re = /^[0-9a-zA-Z\b]+$/
    if (buzCode !== '' && !re.test(buzCode)) return
    setBuzzCode(buzCode)
  }

  const handleSubmit = useCallback(
    (event) => {
      event.preventDefault()
      const form = event.currentTarget

      if (form.checkValidity() === false) {
        event.stopPropagation()
      }
      setValidated(true)
      onSave(buzzCode)
      onHide()
    },
    [buzzCode, onSave, onHide]
  )

  // Directly assign modalType["buzzerCode"] to modalData
  const modalData = {
    title: t('delivery_preferences.buzzer_code'),
    icon: BuzzerCodeIcon,
    saveText: t('buttonActions.save'),
    cancelText: t('buttonActions.cancel'),
    component: (
      <BuzzerCode
        id="buzzerCode"
        buzzerCode={buzzCode}
        onValueChangeHandler={buzzCodeValueChangeHandler}
      />
    ),
    isSaveDisabled: buzzCode === buzzerCode,
    onCancel: () => {
      setBuzzCode(buzzerCode)
      onHide()
    },
  }

  // Don't render the form if modal is not shown
  if (!show) {
    return null
  }

  return (
    <StyledForm id="buzzerCodeForm" noValidate validated={validated} onSubmit={handleSubmit}>
      <StyledPopUp show={show} backdrop="static" centered>
        <StyledPopUp.Header>
          {modalData.icon && <img className="icon-padding" src={modalData.icon} alt="icon-img" />}
          <StyledPopUp.Title>{modalData.title}</StyledPopUp.Title>
        </StyledPopUp.Header>
        <StyledPopUp.Body>{modalData.component}</StyledPopUp.Body>
        <StyledPopUp.Footer>
          <Button variant="secondary" onClick={modalData.onCancel} className="popUpSecBtn">
            {modalData.cancelText}
          </Button>
          <Button
            type="submit"
            variant="primary"
            form="buzzerCodeForm"
            disabled={modalData.isSaveDisabled}
            className="popUpPrimBtn"
          >
            {modalData.saveText}
          </Button>
        </StyledPopUp.Footer>
      </StyledPopUp>
    </StyledForm>
  )
}

BuzzerCodePopUp.propTypes = {
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
}

export default BuzzerCodePopUp
